import React, { useState } from 'react';
import { Smartphone, Cloud, AlertTriangle, ArrowRight } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

/**
 * Modal shown when there's a conflict between local and cloud collections
 * User must choose which collection to keep
 */
export default function CollectionConflictModal({
  isOpen,
  onClose,
  onResolve,
  localData,
  cloudData
}) {
  const { t } = useI18n();
  const [isResolving, setIsResolving] = useState(false);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return t('conflictModal.unknown');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('conflictModal.unknown');
    }
  };

  const handleResolve = async (choice) => {
    setIsResolving(true);
    await onResolve(choice);
    setIsResolving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {t('conflictModal.title')}
              </h2>
              <p className="text-sm text-slate-600">
                {t('conflictModal.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Local Collection Option */}
          <button
            onClick={() => handleResolve('local')}
            disabled={isResolving}
            className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                <Smartphone size={24} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  {t('conflictModal.localCollection')}
                  <ArrowRight size={16} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                </h3>
                <div className="mt-2 space-y-1 text-sm">
                  <p className="text-slate-600">
                    <span className="font-medium text-slate-800">{localData?.count || 0}</span> {t('conflictModal.markers')}
                  </p>
                  <p className="text-slate-500">
                    {t('conflictModal.lastEdited')}: {formatDate(localData?.timestamp)}
                  </p>
                </div>
                <p className="mt-2 text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('conflictModal.uploadToCloud')}
                </p>
              </div>
            </div>
          </button>

          {/* Cloud Collection Option */}
          <button
            onClick={() => handleResolve('cloud')}
            disabled={isResolving}
            className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                <Cloud size={24} className="text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  {t('conflictModal.cloudCollection')}
                  <ArrowRight size={16} className="text-slate-400 group-hover:text-green-500 transition-colors" />
                </h3>
                <div className="mt-2 space-y-1 text-sm">
                  <p className="text-slate-600">
                    <span className="font-medium text-slate-800">{cloudData?.count || 0}</span> {t('conflictModal.markers')}
                  </p>
                  <p className="text-slate-500">
                    {t('conflictModal.lastEdited')}: {formatDate(cloudData?.timestamp)}
                  </p>
                </div>
                <p className="mt-2 text-xs text-green-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('conflictModal.downloadToLocal')}
                </p>
              </div>
            </div>
          </button>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <p>{t('conflictModal.warning')}</p>
          </div>
        </div>

        {/* Loading overlay */}
        {isResolving && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-slate-600">{t('conflictModal.resolving')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
